import React from 'react';

import {
    TooltipContentPlacementInUrl,
    TooltipInURLCoordsType,
    TooltipInURLSizeType,
    TooltipInURLType,
    TooltipSizesInURLType,
    TooltipSizesType,
    TooltipSizeType,
    TooltipSizeVariantInURLType,
    TooltipSizeVariantType,
    TooltipsQueryParamType,
    TooltipsType,
    TooltipTwoLinesSizeTextType,
} from 'src/components/ReviewViewer/Tooltips/types';
import {
    TooltipContentPlacementFromDefaultToURL,
    TooltipContentPlacementFromURLToDefault,
    TooltipSizesPlacementFromDefaultToURL,
    TooltipSizesPlacementFromURLToDefault,
} from 'src/components/ReviewViewer/Tooltips/constants';
import {
    isSizeInURLTwoLineVariant,
    isSizeTwoLineVariant,
} from 'src/components/ReviewViewer/Tooltips/helpers';

import { TooltipOptions } from './types';
import { defaultTooltipOptions } from './constants';

const getCoords = (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>,
    canvas: HTMLCanvasElement,
): { x: number; y: number } => {
    const canvasRect = canvas.getBoundingClientRect();

    const y = Number((((event.clientY - canvasRect.top) / canvasRect.height) * 100).toFixed(2));
    const x = Number((((event.clientX - canvasRect.left) / canvasRect.width) * 100).toFixed(2));

    return { x, y };
};

const decodeTooltipSizesURL = (sizes: TooltipSizesInURLType): TooltipSizesType => {
    const preparedSizes: TooltipSizeType[] = sizes[1].map<TooltipSizeType>(size => {
        if (isSizeInURLTwoLineVariant(size)) {
            const text = size[4];
            const preparedText: TooltipTwoLinesSizeTextType | null = {
                text: text[0],
                ...(text[1]
                    ? {
                          footnoteStartPointCord: {
                              x: text[1][0],
                              y: text[1][1],
                          },
                      }
                    : {}),
            };

            return {
                variant: TooltipSizeVariantType.TwoLines,
                pointCoords: [
                    { x: size[1][0][0], y: size[1][0][1] },
                    { x: size[1][1][0], y: size[1][1][1] },
                ],
                sizeDisplayCoords: { x: size[2][0], y: size[2][1] },
                position: TooltipSizesPlacementFromURLToDefault[size[3]],
                text: preparedText,
            };
        } else {
            return {
                variant: TooltipSizeVariantType.Dot,
                objectCoords: { x: size[1][0], y: size[1][1] },
                sizeDisplayCoords: { x: size[2][0], y: size[2][1] },
                text: { top: size[3][0], bottom: size[3][1] },
            };
        }
    });

    return {
        frameIndex: sizes[0],
        sizes: preparedSizes,
    };
};

const decodeTooltipsURL = (queryParam?: string): TooltipsType | null => {
    if (!queryParam) return null;

    try {
        const parsedTooltipsQueryParam = JSON.parse(queryParam) as TooltipsQueryParamType;

        return parsedTooltipsQueryParam[0].map(([text, coords, sizes, contentPlacement]) => {
            const preparedSizes: TooltipSizesType | null = sizes
                ? decodeTooltipSizesURL(sizes)
                : null;

            return {
                text,
                coords: new Map(coords.map(([frameIndex, x, y]) => [frameIndex, { x, y }])),
                ...(contentPlacement
                    ? {
                          contentPlacement:
                              TooltipContentPlacementFromURLToDefault[contentPlacement],
                      }
                    : {}),
                ...(preparedSizes ? { sizes: preparedSizes } : {}),
            };
        });
    } catch (e) {
        console.error(e);
        return null;
    }
};

const encodeTooltipSizesURL = (sizes: TooltipSizesType): TooltipSizesInURLType => {
    const preparedSizes: TooltipInURLSizeType[] = sizes.sizes.map(size => {
        if (isSizeTwoLineVariant(size)) {
            const footnoteStartPointCord = size.text.footnoteStartPointCord;
            const preparedTextFootnoteStartPointCord = footnoteStartPointCord
                ? ([[footnoteStartPointCord.x, footnoteStartPointCord.y]] as [[number, number]])
                : ([] as []);

            return [
                TooltipSizeVariantInURLType.TwoLines,
                [
                    [size.pointCoords[0].x, size.pointCoords[0].y],
                    [size.pointCoords[1].x, size.pointCoords[1].y],
                ],
                [size.sizeDisplayCoords.x, size.sizeDisplayCoords.y],
                TooltipSizesPlacementFromDefaultToURL[size.position],
                [size.text.text, ...preparedTextFootnoteStartPointCord],
            ];
        } else {
            return [
                TooltipSizeVariantInURLType.Dot,
                [size.objectCoords.x, size.objectCoords.y],
                [size.sizeDisplayCoords.x, size.sizeDisplayCoords.y],
                [size.text.top || '', size.text.bottom || ''],
            ];
        }
    });

    return [sizes.frameIndex, preparedSizes];
};

const encodeTooltipsURL = (
    tooltips: TooltipsType,
    hideWhileSpinning: boolean,
    changeVisibilityNearExtremeFrames: boolean,
): string =>
    JSON.stringify([
        tooltips.map<TooltipInURLType>(({ text, coords, sizes, contentPlacement }) => {
            const content = contentPlacement
                ? ([TooltipContentPlacementFromDefaultToURL[contentPlacement]] as [
                      TooltipContentPlacementInUrl,
                  ])
                : ([] as []);

            const preparedCoords: TooltipInURLCoordsType = [];
            for (const [key, value] of coords.entries()) {
                preparedCoords[preparedCoords.length] = [key, value.x, value.y];
            }

            const preparedSizes: TooltipSizesInURLType = sizes
                ? encodeTooltipSizesURL(sizes)
                : [0, []];

            return [text, preparedCoords, preparedSizes, ...content];
        }),
        hideWhileSpinning,
        changeVisibilityNearExtremeFrames,
    ]);

const decodeTooltipOptionsURL = (queryParam?: string): TooltipOptions => {
    if (!queryParam) return defaultTooltipOptions;

    try {
        const parsedTooltipsQueryParam = JSON.parse(queryParam) as TooltipsQueryParamType;

        return {
            hideWhileSpinning: parsedTooltipsQueryParam[1],
            changeVisibilityNearExtremeFrames: parsedTooltipsQueryParam[2],
        };
    } catch (e) {
        console.error(e);
        return defaultTooltipOptions;
    }
};

export { getCoords, decodeTooltipsURL, encodeTooltipsURL, decodeTooltipOptionsURL };
